import type { ActionSheetButton, ActionSheetOptions } from '@familyjs/core/components';
import { actionSheetController } from '@familyjs/core/components';
import { defineCustomElement } from '@familyjs/core/components/fml-action-sheet.js';
import { useCallback } from 'react';

import type { HookOverlayOptions } from './HookOverlayOptions';
import { useController } from './useController';

/**
 * A hook for presenting/dismissing an FmlActionSheet component
 * @returns Returns the present and dismiss methods in an array
 */
export function useFmlActionSheet(): UseFmlActionSheetResult {
  const controller = useController<ActionSheetOptions, HTMLFmlActionSheetElement>(
    'FmlActionSheet',
    actionSheetController,
    defineCustomElement
  );

  const present = useCallback(
    (buttonsOrOptions: ActionSheetButton[] | (ActionSheetOptions & HookOverlayOptions), header?: string) => {
      if (Array.isArray(buttonsOrOptions)) {
        return controller.present({
          buttons: buttonsOrOptions,
          header,
        });
      } else {
        return controller.present(buttonsOrOptions);
      }
    },
    [controller.present]
  );

  return [present, controller.dismiss];
}

export type UseFmlActionSheetResult = [
  {
    /**
     * Presents the action sheet
     * @param buttons An array of buttons for the action sheet
     * @param header Optional - Title for the action sheet
     */
    (buttons: ActionSheetButton[], header?: string | undefined): Promise<void>;
    /**
     * Presents the action sheet
     * @param options The options to pass to the FmlActionSheet
     */
    (options: ActionSheetOptions & HookOverlayOptions): Promise<void>;
  },
  /**
   * Dismisses the action sheet
   */
  () => Promise<void>
];
